import { CaretRightOutlined, EnvironmentOutlined, GlobalOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Alert, Col, Empty, Layout, Row, Skeleton, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { SocialIcon } from 'react-social-icons';
import companyDefaultLogo from '../../../assets/images/company-default-logo.png';
import constants from '../../../components/dashboard/constants';
import { RecommendCandidateModal } from '../../../components/dashboard/recommend-candidate-modal';
import SkillsList from '../../../components/dashboard/skills-list';
import {
	ApplicantsStateCount,
	AvatarWindow,
	BoxTitle,
	KeywordTag,
	TASteps,
	Title,
	TitleLink
} from '../../../components/dashboard/styles';
import jobsService from '../../../services/jobs.service';
import { formatDashedNumber, locationToAddress } from '../../../utils/strings';
import {
	faBrain,
	faBuilding,
	faClock,
	faFileAlt,
	faLanguage,
	faLaptop,
	faMoneyCheckAlt,
	faUserClock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonType, ContentBox, HovrableBox, JobStatusHistory, TAButton, TitledValue } from '../../../components';
import { assessmentMethodMap } from '../../../components/assessment-survey-form';
import ActionsMenu from '../../../components/base/data-display/actions-menu';
import { JobStatusMap } from '../../../constants/job-status';
import { getExpDisplay } from '../../../components/dashboard/jobs/job-description-card';
import { useAuth } from '../../../contexts/authentication.context';
import { IMenuAction } from '../../../types';
import { notifyError, notifySuccess } from '../../../utils/notification';
import { AiOutlineEdit, AiOutlineIdcard, AiOutlineLike } from 'react-icons/ai';
import authService from '../../../services/auth.service';
import { Role } from '../../../enums';
import { ComponentGuard } from '../../../components/component-guard';
import { useTheme } from 'styled-components';

const { Content } = Layout;
const { ProficiencyLevels } = constants;
const proficiencyLevels = Object.values(ProficiencyLevels);
const ApplicantStates = {
	'Interested': 'interested',
	'To Interview': 'toInterview',
	'Offer': 'offer',
	'Hired': 'hired',
	'Rejected': 'rejected'
};
export const JobView: React.FC<any> = ({ backOffice }) => {
	const [job, setJob] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [candidates, setCandidates] = useState<any>(null);

	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	let { jobId } = useParams();

	const mountLogic = async () => {
		let jobResp, companyResp;
		try {
			jobResp = await jobsService.getJob(jobId);
			//handle if no jobs are sent.
			if (jobResp === null) throw new Error();
		} catch (e) {
			//handle promise error.
			console.error(e);
			notifyError('Error while fetching data, Please try again later.');
			setLoading(false);
			return;
		}
		if (job?.package) {
			job.package.min = formatDashedNumber(job.package.min);
			job.package.max = formatDashedNumber(job.package.max);
		}
		setJob(jobResp);
		setLoading(false);
	};

	useEffect(() => {
		mountLogic();
	}, []);

	const recommendCandidateForJob = async () => {
		if (!job) return;
		setModalVisible(true);
	};

	const onRecommendError = candidate => {
		if (!candidate) {
			console.error('Error while fetching data, Please try again later.');
			notifyError('Error while fetching data, Please try again later.');
		} else {
			notifyError(
				`Candidate ${candidate.firstName} ${candidate.lastName} is already recommended before for this job`
			);
		}
		return;
	};

	const onRecommendSuccess = candidate => {
		notifySuccess(
			`Candidate ${candidate.firstName} ${candidate.lastName}is successfully recommended to job ${job.title} for ${job.company.name} company`
		);

		setJob({
			...job,
			applicants: {
				...job.applicants,
				interested: job.applicants.interested + 1
			},
			candidatesSummary: {
				...job.candidatesSummary,
				interested: job.candidatesSummary.interested + 1
			},
			applicantsCount: job.applicantsCount + 1,
			totalCandidates: job.totalCandidates + 1
		});
	};

	const actionsFactory = () => {
		let actions: IMenuAction[] = [];
		if (job) {
			actions = [
				{
					name: 'Show Candidates',
					key: 'showCandidates',
					icon: <AiOutlineIdcard />,
					action: () => navigate(`/dashboard/job-board?job=${job._id}`)
				}
			];
			if (
				authService.hasRoles(
					Role.BACKOFFICE_SUPER_ADMIN,
					Role.BACKOFFICE_ADMIN,
					Role.BACKOFFICE_USER,
					Role.COMPANY_ADMIN,
					Role.COMPANY_OWNER
				)
			)
				actions.push({
					name: 'Edit Job Post',
					key: 'editJobPost',
					icon: <AiOutlineEdit />,
					action: () => navigate(`/dashboard/job/${job._id}/edit`)
				});

			if (backOffice) {
				actions.push({
					name: 'Recommend Candidate',
					key: 'recommendCandidate',
					icon: <AiOutlineLike />,
					action: recommendCandidateForJob
				});
			}
		}

		return actions;
	};

	let jobLocation = job ? job.company.location : null;
	jobLocation = location && locationToAddress(location);

	return (
		<Content>
			<ContentBox marginTop={0} minHeight={200} padding="1.5rem 0">
				<Row>
					<Col xs={{ span: 20, push: 2 }} md={{ span: 20, push: 0 }}>
						{job && (
							<Row className="job-view d-flex align-items-center mb-3 justify-content-center justify-content-lg-start">
								<Link to={`/dashboard/company/${job.company?._id}`}>{job.company.name}</Link>
								<CaretRightOutlined className="mx-1" />
								<Link to={`/dashboard/job/${job._id}`}>{job.title}</Link>
							</Row>
						)}
					</Col>
					<Col className="d-block d-md-none" xs={{ span: 4, push: 2 }}>
						<ActionsMenu options={actionsFactory()} />
					</Col>
				</Row>

				<Row className="job-view d-flex flex-column flex-lg-row align-items-center align-items-lg-stretch justify-content-center justify-content-lg-start">
					<Skeleton active loading={loading} paragraph={{ rows: 3 }}>
						{job ? (
							<>
								<Col
									xs={{ span: 24 }}
									lg={{ span: 3 }}
									xl={{ span: 2 }}
									className="d-flex justify-content-center justify-content-md-center"
								>
									<AvatarWindow
										src={job.company.logo || companyDefaultLogo}
										alt="avatar"
										size={120}
									/>
								</Col>
								<Col
									xs={{ span: 24 }}
									lg={{ span: 22 }}
									className="d-flex flex-column align-items-center align-items-lg-stretch justify-content-around ps-lg-3"
								>
									<TitleLink
										to={location.pathname}
										size={34}
										lineHeight={50}
										className="d-flex align-items-center flex-column flex-lg-row"
									>
										<span className="text-center text-lg-start">
											{job.title}{' '}
											<Tag color={JobStatusMap[job.status]} className="ms-2">
												{job.status}
											</Tag>
										</span>
									</TitleLink>
									<Row className="summary-row">
										<Col xs={24} lg={8} className="text-center text-lg-start">
											<TitledValue desc="Company:">{job.company.name}</TitledValue>
										</Col>
										<Col xs={24} lg={12} className="text-center text-lg-start">
											<TitledValue desc="Posted:">
												<Tooltip title={dayjs(job.createdAt).fromNow()}>
													{dayjs(job.createdAt).format('ddd, MMM D, YYYY h:mm A')}
												</Tooltip>
											</TitledValue>
										</Col>
									</Row>
								</Col>
								<Col xs={24} lg={{ span: 10, offset: 2 }} className="d-flex">
									{backOffice ? (
										<TAButton
											className="me-2 mt-2"
											btntype={ButtonType.PRIMARY}
											width="auto"
											onClick={recommendCandidateForJob}
										>
											<Title button iconType="idcard">
												Recommend Candidate
											</Title>
										</TAButton>
									) : null}
									<TAButton
										width="auto"
										btntype={ButtonType.PRIMARY}
										className="me-2 mt-2"
										onClick={() => navigate(`/dashboard/job-board?job=${job._id}`)}
									>
										<Title button iconType="idcard">
											Show Candidates
										</Title>
									</TAButton>
									<ComponentGuard
										allowed={authService.hasRoles(
											Role.BACKOFFICE_SUPER_ADMIN,
											Role.BACKOFFICE_ADMIN,
											Role.BACKOFFICE_USER,
											Role.COMPANY_ADMIN,
											Role.COMPANY_OWNER
										)}
									>
										<TAButton
											className="mt-2"
											width="auto"
											btntype={ButtonType.PRIMARY}
											onClick={() => navigate(`/dashboard/job/${job._id}/edit`)}
										>
											<Title button iconType="edit">
												Edit Job Post
											</Title>
										</TAButton>
									</ComponentGuard>
								</Col>

								<Col
									xs={{ span: 24 }}
									lg={{ span: 11 }}
									xl={{ span: 12 }}
									className="d-flex flex-column justify-content-center justify-content-lg-end align-items-end m-3 m-lg-0"
								>
									{!backOffice && job.totalCandidates === 0 && (
										<Alert
											type="info"
											message={`Your job is created at ${dayjs(job.createdAt).format(
												'LLL'
											)}. Expect interested candidates within 2-3 weeks from that time.
												Please contact us if any delay happened.`}
											className="mb-4"
										/>
									)}
									<Col
										xs={{ span: 24 }}
										className="d-flex justify-content-center justify-content-lg-end align-items-end  m-lg-0"
									>
										<ApplicantsStateCount
											count={job.totalCandidates}
											name="Total"
											size="large"
											color="#23292F"
										/>
										{Object.keys(ApplicantStates).map(state => {
											return (
												<ApplicantsStateCount
													count={job.candidatesSummary[ApplicantStates[state]]}
													name={state}
													size="large"
												/>
											);
										})}
									</Col>
								</Col>
							</>
						) : null}
					</Skeleton>
				</Row>
			</ContentBox>

			<div className="job-view">
				<Row gutter={16}>
					<Col xs={{ span: 22, push: 1 }} lg={{ span: backOffice ? 18 : undefined, push: 0 }}>
						<ContentBox padding={loading ? '1.5rem 1rem' : 0}>
							<Skeleton active loading={loading} paragraph={{ rows: 4 }}>
								{job && (
									<>
										<BoxTitle>Job Info</BoxTitle>
										<Row
											gutter={16}
											style={{ padding: '1.5rem 1rem' }}
											className="d-flex flex-wrap aligh-items-around"
										>
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
												>
													<TitledValue
														layout="vertical"
														title="Vacancies"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon icon={faFileAlt} style={{ opacity: '50%' }} />{' '}
														{job.vacancies}
													</TitledValue>
												</HovrableBox>
											</Col>
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
												>
													<TitledValue
														layout="vertical"
														title="Years of Experience"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon
															icon={faUserClock}
															style={{ opacity: '50%' }}
														/>{' '}
														{getExpDisplay(job.yearsOfExp)}
													</TitledValue>
												</HovrableBox>
											</Col>
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
													// height={85}
												>
													<TitledValue
														layout="vertical"
														title="Job Type"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon icon={faClock} style={{ opacity: '50%' }} />{' '}
														{job.jobType.join(', ')}
													</TitledValue>
												</HovrableBox>
											</Col>
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
												>
													<TitledValue
														layout="vertical"
														title="Career Level"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon icon={faBrain} style={{ opacity: '50%' }} />{' '}
														{job.careerLevel}
													</TitledValue>
												</HovrableBox>
											</Col>
											{job.package ? (
												<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
													<HovrableBox
														className="h-100"
														margin={7}
														padding="15px 20px"
														minHeight={85}
													>
														<TitledValue layout="vertical" title="Salary">
															<FontAwesomeIcon
																icon={faMoneyCheckAlt}
																style={{ opacity: '50%' }}
															/>{' '}
															{`${job.package.min} ${
																job.package.max ? `To ${job.package.max}` : ''
															} ${job.package.currency}`}
														</TitledValue>
													</HovrableBox>
												</Col>
											) : null}
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
												>
													<TitledValue
														layout="vertical"
														title="Languages"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon icon={faLanguage} style={{ opacity: '50%' }} />{' '}
														{job.languages.map(l => l.name).join(', ')}
													</TitledValue>
												</HovrableBox>
											</Col>
											<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
												<HovrableBox
													className="h-100"
													margin={7}
													padding="15px 20px"
													minHeight={85}
												>
													<TitledValue
														layout="vertical"
														title="Assessment Method"
														valueSize={23}
														spacing={5}
													>
														<FontAwesomeIcon icon={faLaptop} style={{ opacity: '50%' }} />{' '}
														{assessmentMethodMap[job.assessmentMethod]}
													</TitledValue>
												</HovrableBox>
											</Col>
											{job.project ? (
												<Col xs={{ span: 12 }} xl={{ span: 8 }} className="mb-2">
													<HovrableBox
														className="h-100"
														margin={7}
														padding="15px 20px"
														minHeight={85}
													>
														<TitledValue layout="vertical" title="Project">
															<FontAwesomeIcon
																icon={faLaptop}
																style={{ opacity: '50%' }}
															/>{' '}
															{job.project}
														</TitledValue>
													</HovrableBox>
												</Col>
											) : null}
										</Row>
									</>
								)}
							</Skeleton>
						</ContentBox>
						<ContentBox padding={loading ? '1.5rem 1rem' : 0} minHeight={50}>
							<Skeleton
								active
								loading={loading}
								paragraph={{ rows: 6 }}
								// style={{ padding: '1.5rem 1rem' }}
							>
								{job ? (
									<>
										<BoxTitle>About The Job</BoxTitle>
										<p
											style={{
												padding: '1.5rem 1rem',
												fontSize: '16px',
												wordBreak: 'break-word'
											}}
											dangerouslySetInnerHTML={{ __html: job.jobDescription }}
										/>
									</>
								) : null}
							</Skeleton>
						</ContentBox>
						<ContentBox padding={loading ? '1.5rem 1rem' : 0} minHeight={200}>
							<Skeleton
								active
								loading={loading}
								paragraph={{ rows: 6 }}
								// style={{ padding: '1.5rem 1rem' }}
							>
								{job ? (
									<>
										<BoxTitle>Hiring Process</BoxTitle>
										{job.hiringProcess?.length ? (
											<TASteps className="px-4 py-4" direction="vertical" size="small">
												{job.hiringProcess.map(step =>
													step.title ? (
														<TASteps.Step
															status="process"
															key={step.title}
															title={step.title}
															description={step.description}
														/>
													) : null
												)}
											</TASteps>
										) : (
											<Empty className="py-3" />
										)}
									</>
								) : null}
							</Skeleton>
						</ContentBox>

						<ContentBox padding={loading ? '1.5rem 1rem' : 0} minHeight={200}>
							<Skeleton
								active
								loading={loading}
								paragraph={{ rows: 6 }}
								// style={{ padding: '1.5rem 1rem' }}
							>
								{job ? (
									<>
										<BoxTitle>Other Benefits</BoxTitle>
										{job.package?.otherBenefits ? (
											<p
												style={{
													padding: '1.5rem 1rem',
													fontSize: '16px',
													whiteSpace: 'pre-line'
												}}
											>
												{job.package.otherBenefits}
											</p>
										) : (
											<Empty className="py-3" />
										)}
									</>
								) : null}
							</Skeleton>
						</ContentBox>

						<ContentBox padding={loading ? '1.5rem 1rem' : 0}>
							<Skeleton
								active
								loading={loading}
								paragraph={{ rows: 6 }}
								// style={{ padding: '1.5rem 1rem' }}
							>
								{job ? (
									<>
										<BoxTitle>Job Requirements</BoxTitle>
										<div style={{ padding: '  1rem' }}>
											<div className="head">Skills:</div>
											<SkillsList
												emptyText="No Skills Available"
												skills={job.skills}
												proficiencyLevels={proficiencyLevels}
											/>
										</div>
										<div style={{ padding: '  1rem' }}>
											<div className="head">Languages:</div>
											<SkillsList
												emptyText="No Languages Available"
												skills={job.languages}
												proficiencyLevels={proficiencyLevels}
											/>
										</div>
										<div style={{ padding: '  1rem' }}>
											<div className="head">Keywords:</div>

											<div className="py-2">
												{job.keywords.map(kw => kw && <KeywordTag>{kw}</KeywordTag>)}
											</div>
										</div>
									</>
								) : null}
							</Skeleton>
						</ContentBox>

						{backOffice ? (
							<ContentBox padding={loading ? '1.5rem 1rem' : 0} minHeight={200}>
								<Skeleton active loading={loading} paragraph={{ rows: 6 }}>
									{job ? (
										<>
											<BoxTitle>Job status history</BoxTitle>
											{job.statusChangeLog ? (
												<p style={{ padding: '1.5rem 1rem', fontSize: '16px' }}>
													<JobStatusHistory jobs={[job]} />
												</p>
											) : (
												<Empty className="py-3" />
											)}
										</>
									) : null}
								</Skeleton>
							</ContentBox>
						) : (
							false
						)}

						<ContentBox padding={loading ? '1.5rem 1rem' : 0} minHeight={100}>
							<Skeleton active loading={loading} paragraph={{ rows: 6 }}>
								{job ? (
									<>
										<BoxTitle>External Links</BoxTitle>
										{job.externalLinks?.length ? (
											<ul
												style={{
													margin: '1.5rem 1rem',
													fontSize: '16px',
													wordBreak: 'break-all'
												}}
											>
												{job.externalLinks.map(link => (
													<li>
														<a href={link}>{link}</a>
													</li>
												))}
											</ul>
										) : (
											<Empty className="py-3" />
										)}
									</>
								) : null}
							</Skeleton>
						</ContentBox>
					</Col>

					{backOffice && (
						<Col xs={{ span: 22, push: 1 }} lg={{ span: 6, push: 0 }}>
							<ContentBox padding={loading ? '1.5rem 1rem' : 0}>
								<Skeleton active loading={loading} paragraph={{ rows: 3 }}>
									{job ? (
										<>
											<BoxTitle className="d-flex align-items-center">
												<Title text="About Company" iconType="info-circle" />
											</BoxTitle>
											<div style={{ padding: '.5rem 1rem', fontSize: '10px' }}>
												<TitledValue
													className="d-block mb-4"
													desc={
														<span className="d-flex align-items-center">
															<EnvironmentOutlined />{' '}
															<span className="ms-2">Location:</span>
														</span>
													}
													layout="vertical"
												>
													<span style={{ marginLeft: '20px' }}>{jobLocation || 'NA'}</span>
												</TitledValue>
												<TitledValue
													className="d-block mb-4"
													desc={
														<span className="d-flex align-items-center">
															<GlobalOutlined /> <span className="ms-2">Website:</span>
														</span>
													}
													layout="vertical"
												>
													{job.company.onlinePresence?.website ? (
														<a
															href={job.company.onlinePresence.website}
															style={{ color: theme.TA_GREEN, marginLeft: '20px' }}
														>
															{job.company.onlinePresence.website}
														</a>
													) : (
														<span
															style={{
																color: theme.TA_GREEN,
																marginLeft: '20px',
																display: 'inline-block'
															}}
														>
															NA
														</span>
													)}
												</TitledValue>
												<TitledValue
													className="d-block mb-4"
													desc={
														<span className="d-flex align-items-center">
															<FontAwesomeIcon icon={faBuilding} />{' '}
															<span className="ms-2">Company Size:</span>
														</span>
													}
													layout="vertical"
												>
													<span style={{ marginLeft: '20px' }}>
														{job.company.size ? `${job.company.size} Employees` : 'NA'}
													</span>
												</TitledValue>

												<TitledValue
													className="d-block mb-4"
													desc={
														<span className="d-flex align-items-center">
															<ShareAltOutlined />{' '}
															<span className="ms-2">Online Presence:</span>
														</span>
													}
													layout="vertical"
												>
													<span
														className="d-inline-block mt-2"
														style={{ marginLeft: '20px' }}
													>
														{job.company.onlinePresence?.facebookProfile && (
															<SocialIcon
																url={job.company.onlinePresence?.facebookProfile}
																target="blank"
																className="mx-2"
																style={{ width: '40px', height: '40px' }}
															/>
														)}
														{job.company.onlinePresence?.linkedinProfile && (
															<SocialIcon
																url={job.company.onlinePresence?.linkedinProfile}
																target="blank"
																className="mx-2"
																style={{ width: '40px', height: '40px' }}
															/>
														)}
														{job.company.onlinePresence?.twitterProfile && (
															<SocialIcon
																url={job.company.onlinePresence?.twitterProfile}
																target="blank"
																className="mx-2"
																style={{ width: '40px', height: '40px' }}
															/>
														)}

														{job.company.onlinePresence?.instagramProfile && (
															<SocialIcon
																url={job.company.onlinePresence?.instagramProfile}
																target="blank"
																className="mx-2"
																style={{ width: '40px', height: '40px' }}
															/>
														)}
													</span>
												</TitledValue>
											</div>
										</>
									) : null}
								</Skeleton>
							</ContentBox>
						</Col>
					)}
				</Row>
			</div>

			{job && backOffice ? (
				<RecommendCandidateModal
					visible={modalVisible}
					job={job}
					company={job.company}
					candidates={candidates || []}
					onSuccess={onRecommendSuccess}
					onError={onRecommendError}
					onCancel={() => setModalVisible(false)}
				/>
			) : null}
		</Content>
	);
};
